import {
  createContext,
  useContext,
  useState,
  useRef,
  useMemo,
  useCallback,
  useEffect,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import { useMatchLogStore } from "../../../store/useMatchLogStore";
import { useMatchSocket } from "../../../hooks/useMatchSocket";
import {
  IS_E2E_TEST_MODE,
  updateAttackDirection,
} from "../../../lib/loggerApi";
import {
  computeFieldFlip,
  displayPeriodForStatus,
  isHomeAttackingLeft,
} from "../utils/attackDirection";
import { useManualFlipOverride } from "../hooks/useManualFlipOverride";
import { useActionFlow } from "../hooks/useActionFlow";
import { useMatchTimer } from "../hooks/useMatchTimer";
import { usePeriodManager, type PeriodPhase } from "../hooks/usePeriodManager";
import {
  computeBroadcastClock,
  formatStampedMatchClock,
  resolveBroadcastArgument,
} from "../lib/clockHelpers";
import type { CardSelection } from "../components/molecules/QuickCardPanel";
import { useDisciplinary } from "../hooks/useDisciplinary";
import { useGoalTally } from "../hooks/useGoalTally";
import { useLiveScore } from "../hooks/useLiveScore";
import { useDuplicateTelemetry } from "../hooks/useDuplicateTelemetry";
import { useOnFieldRoster } from "../hooks/useOnFieldRoster";
import { useClockDrift } from "../hooks/useClockDrift";
import { useMatchData } from "../hooks/useMatchData";
import { useVarTimer } from "../hooks/useVarTimer";
import { useTimeoutTimer } from "../hooks/useTimeoutTimer";
import { useIneffectiveTime } from "../hooks/useIneffectiveTime";
import { useResetMatch } from "../hooks/useResetMatch";
import { useCockpitHarness } from "../hooks/useCockpitHarness";
import { useCockpitEventHandlers } from "../hooks/useCockpitEventHandlers";
import { useCockpitClockHandlers } from "../hooks/useCockpitClockHandlers";
import { useCockpitInteractionHandlers } from "../hooks/useCockpitInteractionHandlers";
import { useCockpitKeyboardHandlers } from "../hooks/useCockpitKeyboardHandlers";
import { useCockpitLifecycleEffects } from "../hooks/useCockpitLifecycleEffects";
import { useCockpitHarnessEvents } from "../hooks/useCockpitHarnessEvents";
import { useCockpitAutoEffects } from "../hooks/useCockpitAutoEffects";
import { useCockpitE2EPlayersSeed } from "../hooks/useCockpitE2EPlayersSeed";
import { useCockpitIneffectiveBreakdown } from "../hooks/useCockpitIneffectiveBreakdown";
import { useCockpitLocalEffects } from "../hooks/useCockpitLocalEffects";
import { useCockpitStatusProjection } from "../hooks/useCockpitStatusProjection";
import { useCockpitVarDerivedState } from "../hooks/useCockpitVarDerivedState";
import { useCockpitTimeoutDerivedState } from "../hooks/useCockpitTimeoutDerivedState";
import { useCockpitIneffectiveTickEffect } from "../hooks/useCockpitIneffectiveTickEffect";
import { useCockpitExpelledPlayerEffect } from "../hooks/useCockpitExpelledPlayerEffect";
import { useCockpitTransitionState } from "../hooks/useCockpitTransitionState";
import { useCockpitToast } from "../hooks/useCockpitToast";
import { useCockpitShootout } from "../hooks/useCockpitShootout";
import { useCockpitSubstitutionFlow } from "../hooks/useCockpitSubstitutionFlow";
import {
  useTacticalPositions,
  usePositionGroupSync,
} from "../hooks/useTacticalPositions";
import type { LoggerHarness, Match, Player } from "../types";
import type { CockpitViewMode } from "../components/molecules/TeamSelector";
import { parseClockToSeconds } from "../lib/clockHelpers";
import { useAuthStore } from "../../../store/authStore";
import type { PendingAckEntry } from "../../../store/useMatchLogStore";

declare global {
  interface Window {
    __PROMATCH_LOGGER_HARNESS__?: LoggerHarness;
  }
}

// ---------------------------------------------------------------------------
// Pure helpers — exported for unit testing (PMA-COCK-026)
// ---------------------------------------------------------------------------

/**
 * Determines whether the cockpit is in a "submitting" state that blocks
 * player-action dispatch.
 *
 * GameStoppage events (VAR start/stop, timeout start/stop, ClockStop,
 * ClockStart) are neutral timer bookkeeping sent via logNeutralTimerEvent
 * → sendEvent, bypassing the dispatchEvent guard entirely.  Pending acks
 * for them must NOT block the operator from logging a player-action event —
 * doing so caused BUG-001 (Offside dropped after TimeoutStop, leaving
 * clockMode EFFECTIVE and btn-resume-effective hidden).
 *
 * Only a pending ack for a non-GameStoppage event (i.e. a real player
 * action) should hold the gate open.
 */
export function computeIsSubmitting(
  pendingAcks: Record<string, PendingAckEntry>,
): boolean {
  return Object.values(pendingAcks).some(
    (entry) => entry.event.type !== "GameStoppage",
  );
}

/**
 * Determines whether a global-clock-start attempt is a genuine kickoff
 * (Pending/Scheduled -> Live_First_Half) that must go through the
 * guardTransition lineup pre-check, versus a same-status clock resume that
 * must not.
 *
 * MUST mirror useMatchTimer.ts's handleGlobalClockStart kickoff-vs-resume
 * predicate EXACTLY (`match.status === "Pending" || "Scheduled"` there
 * decides whether it sets startStatus to "Live_First_Half" or leaves it
 * undefined). isTransitionAllowed's FSM map (useTransitionGuards.ts) has no
 * Live_First_Half -> Live_First_Half self-loop, so routing a resume call
 * through guardTransition rejects it outright — that was the PMA-COCK-089
 * regression (qa-epic08, git-stash repro on
 * e2e/logger-substitution-rules.spec.ts:247). If these two predicates ever
 * diverge, kickoff will silently bypass the lineup pre-check or a resume
 * will get wrongly blocked.
 */
export function isKickoffAttemptStatus(
  matchStatus: Match["status"] | undefined,
): boolean {
  return matchStatus === "Pending" || matchStatus === "Scheduled";
}

/**
 * PMA-COCK-152.AC5: whether a match counts as "live" for tactical drag
 * bounds (`useTacticalPositions.ts`'s `getBoundsForPlayer`) and
 * `ActionStage.tsx`'s `substitutionEnabled` hint. Extracted as a pure named
 * export (mirrors `computeIsSubmitting`'s/`isKickoffAttemptStatus`'s pattern
 * above) so the terminal-status exclusion list is unit-testable without
 * mounting the whole `CockpitProvider`.
 *
 * Explicitly names the three terminal `MatchStatus` values
 * (`Fulltime`/`Completed`/`Abandoned`) instead of only widening the
 * pre-PMA-COCK-152 `["Pending","Scheduled"]` exclusion list, so a FUTURE
 * `MatchStatus` value must be classified on purpose rather than defaulting
 * to "live" again.
 */
export function computeIsMatchLive(
  matchStatus: Match["status"] | undefined,
): boolean {
  return (
    !!matchStatus &&
    !["Pending", "Scheduled", "Fulltime", "Completed", "Abandoned"].includes(
      matchStatus,
    )
  );
}

/**
 * PMA-COCK-144 extension: which scoped stoppage(s) closeScopedStoppages must
 * close before usePeriodManager.performTransition resumes EFFECTIVE. Pure
 * decision extracted from closeScopedStoppages (mirrors computeIsSubmitting's
 * pattern above) so the "what to close" question is unit-testable without
 * mounting the whole provider — the actual closing (logNeutralTimerEvent,
 * handleVarToggleBase) stays side-effecting inside the useCallback.
 */
export function computeScopedStoppageCloseKinds(
  isVarActive: boolean,
  isTimeoutActive: boolean,
): Array<"VAR" | "TIMEOUT"> {
  const kinds: Array<"VAR" | "TIMEOUT"> = [];
  if (isVarActive) kinds.push("VAR");
  if (isTimeoutActive) kinds.push("TIMEOUT");
  return kinds;
}

/**
 * PMA-COCK-155 (toast half): whether a null->truthy transition on
 * `useMatchTimer`'s `modeSwitchError` should fire the operator toast this
 * render. `modeSwitchError` is reset to `null` at the START of every new
 * `handleModeSwitch` attempt (see that hook's own doc comment), so a plain
 * "is it truthy" check would re-fire the toast on every re-render while the
 * value stays non-null — this pure predicate makes the one-shot
 * previous-vs-current comparison unit-testable without mounting the whole
 * provider (no render harness exists for this file — see PMA-COCK-101's own
 * coverage note), mirroring `computeIsSubmitting`'s/`isKickoffAttemptStatus`'s
 * established pattern above.
 */
export function shouldShowModeSwitchErrorToast(
  modeSwitchError: string | null,
  previousModeSwitchError: string | null,
): boolean {
  return Boolean(modeSwitchError) && !previousModeSwitchError;
}

// ---------------------------------------------------------------------------
// Context — value type is inferred from the provider's useMemo return.
// Props are ultimately type-checked at child component boundaries.
// ---------------------------------------------------------------------------
const CockpitContext = createContext<any>(null);

export function useCockpit() {
  const ctx = useContext(CockpitContext);
  if (!ctx) throw new Error("useCockpit must be used inside <CockpitProvider>");
  return ctx;
}

// ---------------------------------------------------------------------------
// Provider — calls every cockpit hook, wires them together, and exposes
// the complete value tree via React context.
// ---------------------------------------------------------------------------
export function CockpitProvider({ children }: { children: ReactNode }) {
  const { t, ready: isLoggerReady } = useTranslation("logger");
  const { matchId } = useParams();
  const [searchParams] = useSearchParams();
  const initialView: CockpitViewMode =
    searchParams.get("view") === "analytics"
      ? "analytics"
      : searchParams.get("view") === "review"
        ? "review"
        : "logger";

  // ── Store ──────────────────────────────────────────────────────────────
  const {
    isConnected,
    liveEvents,
    queuedEvents,
    failedEvents,
    pendingAcks,
    undoStack,
    duplicateHighlight,
    duplicateStats,
    operatorClock,
    operatorPeriod,
    isBallInPlay,
    setIsBallInPlay,
    resetOperatorControls,
    setCurrentMatch,
    setLiveEvents,
    upsertLiveEvent,
    updateEventNotes,
    removeQueuedEvent,
    removeUndoCandidate,
    clearDuplicateHighlight,
    resetDuplicateStats,
    clearQueuedEvents,
    clearUndoStack,
    clearPendingAcks,
    removeLiveEventByClientId,
    removeLiveEventById,
    removeQueuedEventByClientId,
    rejectPendingAck,
    lastTimelineRefreshRequest,
    lastMatchRefreshRequest,
  } = useMatchLogStore();

  const currentUser = useAuthStore((state) => state.user);
  const isAdmin = currentUser?.role === "admin";
  const pendingAckCount = Object.keys(pendingAcks).length;
  const isSubmitting = computeIsSubmitting(pendingAcks);
  const queuedCount = queuedEvents.length;
  const failedCount = failedEvents.length;

  // ── Socket ─────────────────────────────────────────────────────────────
  const { sendEvent, undoEvent } = useMatchSocket({
    matchId: matchId!,
    enabled: !!matchId,
  });

  // ── Match data ─────────────────────────────────────────────────────────
  const { match, setMatch, loading, error, fetchMatch, hydrateEvents } =
    useMatchData({ matchId, isLoggerReady, t, setLiveEvents });

  // ── UI state ───────────────────────────────────────────────────────────
  const [selectedTeam, setSelectedTeam] = useState<"home" | "away" | "both">(
    "home",
  );
  const [showSubstitutionFlow, setShowSubstitutionFlow] = useState(false);
  const [substitutionTeam, setSubstitutionTeam] = useState<"home" | "away">(
    "home",
  );
  const [viewMode, setViewMode] = useState<CockpitViewMode>(initialView);
  // EPIC-119 D4: raw operator "Voltear cancha" override. The flag every
  // consumer reads as `manualFieldFlip` is the combined value computed below
  // (real starting side mirrored per period, XOR this override).
  // Reset on every status change (halftime, second-half kickoff, extra time).
  const [manualFlipOverride, setManualFieldFlip] = useManualFlipOverride(
    match?.status,
  );
  const homeAttacksRightFirstHalf = match?.home_attacks_right_first_half;
  const displayPeriod = displayPeriodForStatus(match?.status);
  const manualFieldFlip = computeFieldFlip(
    homeAttacksRightFirstHalf,
    match?.status,
    manualFlipOverride,
  );
  const homeAttackingLeft = isHomeAttackingLeft(
    homeAttacksRightFirstHalf,
    displayPeriod,
  );
  // D3: persist the starting side. Optimistic; rolls back on failure.
  const setHomeAttacksRightFirstHalf = useCallback(
    async (value: boolean) => {
      if (!matchId) return;
      const previous = match?.home_attacks_right_first_half ?? null;
      setMatch((prev) =>
        prev ? { ...prev, home_attacks_right_first_half: value } : prev,
      );
      try {
        await updateAttackDirection(matchId, value);
      } catch (err) {
        console.error("Failed to persist attack direction", err);
        setMatch((prev) =>
          prev ? { ...prev, home_attacks_right_first_half: previous } : prev,
        );
      }
    },
    [matchId, match?.home_attacks_right_first_half, setMatch],
  );
  const [priorityPlayerId, setPriorityPlayerId] = useState<string | null>(null);
  const [pendingCardType, setPendingCardType] = useState<CardSelection | null>(
    null,
  );
  const pendingCardTypeRef = useRef<CardSelection | null>(null);
  const [ineffectiveTick, setIneffectiveTick] = useState(0);
  const [substitutionInitialPlayerOn, setSubstitutionInitialPlayerOn] =
    useState<Player | null>(null);
  const [substitutionInitialPlayerOff, setSubstitutionInitialPlayerOff] =
    useState<Player | null>(null);

  // ── Roster ─────────────────────────────────────────────────────────────
  const { onFieldIds, applyOnFieldChange } = useOnFieldRoster(
    match,
    liveEvents,
    queuedEvents,
  );

  const homeOnFieldPlayers = useMemo(
    () =>
      (match?.home_team.players ?? []).filter((p) => onFieldIds.home.has(p.id)),
    [match, onFieldIds.home],
  );
  const awayOnFieldPlayers = useMemo(
    () =>
      (match?.away_team.players ?? []).filter((p) => onFieldIds.away.has(p.id)),
    [match, onFieldIds.away],
  );

  // ── Tactical positions ─────────────────────────────────────────────────
  // PMA-COCK-152: was `!["Pending", "Scheduled"].includes(status)` — an
  // EXCLUSION list that silently classified "Fulltime"/"Completed"/
  // "Abandoned" (the match-is-OVER statuses, `MatchStatus` in the backend's
  // `app/models/match.py`) as "live" too, since nothing had ever added them
  // to the exclusion. That fed two real behaviors for a finished match:
  // `useTacticalPositions`' drag bounds widened to the full field
  // (`LIVE_HOME_BOUNDS`) instead of the restricted pre/post-match bounds,
  // and `ActionStage.tsx`'s `substitutionEnabled` derivation turned true,
  // showing a "you can reposition AND SUBSTITUTE players" hint on a match
  // that can no longer log a substitution at all (`cockpitLocked` already
  // blocks the actual commit in `useCockpitInteractionHandlers.ts` and
  // `useCockpitSubstitutionFlow.ts` — this was a bounds/copy bug, not a
  // write-path gap). Extracted to `computeIsMatchLive` (PMA-COCK-152.AC5) so
  // the terminal-status exclusion is unit-testable without mounting the
  // whole provider.
  const isMatchLive = computeIsMatchLive(match?.status);

  const {
    positions: tacticalPositions,
    getDisplayPosition,
    movePlayerFromDisplay,
    applySubstitution: applyTacticalSubstitution,
    draggingPlayerId,
    setDraggingPlayerId,
    homeFormation,
    awayFormation,
    applyFormation,
  } = useTacticalPositions({
    matchId,
    homePlayers: homeOnFieldPlayers,
    awayPlayers: awayOnFieldPlayers,
    isMatchLive,
  });

  // Keeps getPositionGroup's override cache in sync with the reference-data
  // API (PMA-REF) so tactical layout uses the real position→group mapping
  // instead of the fuzzy-match fallback. See useTacticalPositions.ts.
  usePositionGroupSync();

  const handleTacticalPlayerDragEnd = useCallback(
    (
      playerId: string,
      displayX: number,
      displayY: number,
      playerPosition: string | undefined,
      side: "home" | "away",
    ) => {
      movePlayerFromDisplay(
        playerId,
        { x: displayX, y: displayY },
        manualFieldFlip,
        playerPosition,
        side,
      );
    },
    [movePlayerFromDisplay, manualFieldFlip],
  );

  const resetSubstitutionPrefill = useCallback(() => {
    setSubstitutionInitialPlayerOn(null);
    setSubstitutionInitialPlayerOff(null);
  }, []);

  // ── Toast ──────────────────────────────────────────────────────────────
  const { toast, setToast, showTimedToast, dismissToast } = useCockpitToast();

  // Task #37: fire the toast once per event that gives up, not on every
  // retry — handleAckResult (useMatchSocket.ts) moves an event to
  // failedEvents exactly once, when its _retryCount reaches the cap, so
  // watching failedCount upticks is a reliable one-shot signal without
  // needing useMatchSocket itself to know about toasts (it stays
  // UI-agnostic; only this page-level context reacts to the state change).
  const previousFailedCountRef = useRef(failedCount);
  useEffect(() => {
    if (failedCount > previousFailedCountRef.current) {
      showTimedToast(
        t(
          "eventFailedToSave",
          "An event failed to save after repeated attempts and was not sent. It has been recorded — see the failed-events indicator.",
        ),
      );
    }
    previousFailedCountRef.current = failedCount;
  }, [failedCount, showTimedToast, t]);

  // ── Ineffective breakdown ──────────────────────────────────────────────
  const ineffectiveBreakdown = useCockpitIneffectiveBreakdown({
    match,
    liveEvents,
    queuedEvents,
    ineffectiveTick,
  });

  // ── VAR timer ──────────────────────────────────────────────────────────
  const {
    isVarActiveLocal,
    varStartMs,
    varStartGlobalSeconds,
    varStartTotalSeconds,
    varPauseStartMs,
    varPausedSeconds,
    varTick,
    syncVarPauseWithClockMode,
    syncVarWithGlobalClockRunning,
    handleVarToggle: handleVarToggleBase,
    handleGlobalClockStartVarSync,
    handleGlobalClockStopVarSync,
    resetVarState,
  } = useVarTimer({ match });

  const breakdownVarActive = Boolean(ineffectiveBreakdown?.varActive);
  const isVarActive = breakdownVarActive || isVarActiveLocal;

  // ── Timeout timer ──────────────────────────────────────────────────────
  const { isTimeoutActive, timeoutTimeSeconds } = useTimeoutTimer({
    ineffectiveBreakdown,
  });

  // ── Match clock ────────────────────────────────────────────────────────
  const {
    globalClock,
    effectiveClock,
    effectiveTime,
    ineffectiveClock,
    clockMode,
    isClockRunning: isGlobalClockRunning,
    clockSnapshotRef,
    modeSwitchError,
    clockActionError,
    handleGlobalClockStart,
    handleGlobalClockStop,
    handleModeSwitch,
  } = useMatchTimer(match, fetchMatch, {
    isVarActive,
    varStartMs,
    varPauseStartMs,
    varPausedSeconds,
    timeoutSeconds: timeoutTimeSeconds,
    isTimeoutActive,
  });

  // PMA-COCK-155: `modeSwitchError` (useMatchTimer.ts) is set once a
  // clock-mode PATCH exhausts every retry attempt — the prod incident's
  // actual failure mode (a dropped PATCH silently diverged the client's
  // clock from the server's for the rest of the match). It is reset to
  // `null` at the START of every new `handleModeSwitch` attempt, so a
  // null→truthy transition is a reliable one-shot signal — same idiom as
  // the `failedCount` toast effect above, just watching a different field.
  // Deliberately does NOT surface `modeSwitchError`'s own raw message text:
  // the operator needs "what does this mean for me" (the mode didn't save,
  // the server may disagree with the screen), not a network error string.
  const previousModeSwitchErrorRef = useRef(modeSwitchError);
  useEffect(() => {
    if (
      shouldShowModeSwitchErrorToast(
        modeSwitchError,
        previousModeSwitchErrorRef.current,
      )
    ) {
      showTimedToast(
        t(
          "modeSwitchFailedToast",
          "The clock mode change did not save after repeated attempts. The server may not match what you see — check the match state before continuing.",
        ),
      );
    }
    previousModeSwitchErrorRef.current = modeSwitchError;
  }, [modeSwitchError, showTimedToast, t]);

  // PMA-COCK-003 v1.179.0, narrowed v1.182.0: `clockActionError`
  // (useMatchTimer.ts) is set only when `handleGlobalClockStop`/
  // `handleGlobalClockReset`'s server round-trip is DELIBERATELY refused —
  // `status === 409`, detected via `isClockActionRefusal` — not on every
  // thrown error. Today this covers two distinct refusals: the
  // `PATCH .../clock-mode` 409 the backend returns unconditionally while the
  // match is in `Penalties` (PMA-MATCH-030), and a `PATCH .../status` 409 on
  // a concurrent-modification conflict. A generic/transient failure (network
  // blip, offline) is still `console.error`-ed but does NOT set this field.
  // Same one-shot null→truthy transition idiom as
  // `modeSwitchError` above (`shouldShowModeSwitchErrorToast` is a plain
  // `Boolean(x) && !previous` predicate with nothing switch-specific in its
  // implementation, so it is reused here rather than duplicated); a
  // separate field and effect because these two handlers have no retry loop
  // to describe in the toast text.
  const previousClockActionErrorRef = useRef(clockActionError);
  useEffect(() => {
    if (
      shouldShowModeSwitchErrorToast(
        clockActionError,
        previousClockActionErrorRef.current,
      )
    ) {
      showTimedToast(
        t(
          "clockActionFailedToast",
          "The clock update did not save. The server may not match what you see — check the match state before continuing.",
        ),
      );
    }
    previousClockActionErrorRef.current = clockActionError;
  }, [clockActionError, showTimedToast, t]);

  // ── VAR derived state ──────────────────────────────────────────────────
  const { varTimeSeconds, varTimeClock } = useCockpitVarDerivedState({
    ineffectiveBreakdown,
    isVarActiveLocal,
    varStartGlobalSeconds,
    varStartTotalSeconds,
    varStartMs,
    varPauseStartMs,
    varPausedSeconds,
    varTick,
    isGlobalClockRunning,
    clockMode,
    syncVarWithGlobalClockRunning,
    syncVarPauseWithClockMode,
    globalClock,
  });

  // ── Timeout derived clock (PMA-COCK-006) ───────────────────────────────
  // Only the exported STRING `timeoutTimeClock` uses this; the numeric
  // `timeoutTimeSeconds` stays useTimeoutTimer's value everywhere.
  const { timeoutDisplayClock: timeoutTimeClock } =
    useCockpitTimeoutDerivedState({
      isTimeoutActive,
      timeoutTimeSeconds,
      globalClock,
      isGlobalClockRunning,
      isMatchLoaded: Boolean(match),
    });

  // ── Ineffective time ──────────────────────────────────────────────────
  // Ref (not a plain value) because the broadcast-cap main clock is derived
  // from `usePeriodManager`, which is wired up below this hook — the ref
  // lets stoppage logging read the latest value without needing that
  // hook-call ordering. Set once `broadcastMatchClock` is computed below.
  const broadcastMatchClockRef = useRef<string>("00:00.000");
  const {
    ineffectiveNoteOpen,
    ineffectiveNoteText,
    setIneffectiveNoteText,
    ineffectiveActionType,
    setIneffectiveActionType,
    ineffectiveTeamSelection,
    setIneffectiveTeamSelection,
    ineffectiveActionDropdownOpen,
    setIneffectiveActionDropdownOpen,
    ineffectiveTeamDropdownOpen,
    setIneffectiveTeamDropdownOpen,
    hasActiveIneffective,
    beginIneffective,
    endIneffectiveIfNeeded,
    confirmIneffectiveNote,
    cancelIneffectiveNote,
    logNeutralTimerEvent,
  } = useIneffectiveTime({
    match,
    selectedTeam,
    matchClockRef: broadcastMatchClockRef,
    operatorPeriod,
    clockMode,
    handleModeSwitch,
    sendEvent,
    setIsBallInPlay,
    setMatch,
    clockSnapshotRef,
  });

  useCockpitIneffectiveTickEffect({
    match,
    hasActiveIneffective,
    clockMode,
    isVarActiveLocal,
    isTimeoutActive,
    setIneffectiveTick,
  });

  // ── Disciplinary ──────────────────────────────────────────────────────
  const { cardDisciplinaryStatus, cardYellowCounts, expelledPlayerIds } =
    useDisciplinary(liveEvents, queuedEvents);

  // ── Goal tally (PMA-COCK-145) ───────────────────────────────────────────
  const goalCountByPlayerId = useGoalTally(liveEvents, queuedEvents);

  // ── Local effects ──────────────────────────────────────────────────────
  useCockpitLocalEffects({
    match,
    matchId,
    setManualFieldFlip,
    setPriorityPlayerId,
    isGlobalClockRunning,
    setIsBallInPlay,
  });

  // ── Status projection ─────────────────────────────────────────────────
  const { statusOverride, matchForPhase } = useCockpitStatusProjection({
    match,
  });

  // ── Drift ──────────────────────────────────────────────────────────────
  const {
    computedDriftSeconds,
    forcedDriftSeconds,
    driftSeconds,
    showDriftNudge,
  } = useClockDrift({
    match,
    globalClock,
    varPauseStartMs,
    varPausedSeconds,
    varTimeSeconds,
    fetchMatch,
  });

  // ── Scoped-stoppage auto-close (PMA-COCK-144 extension) ────────────────
  // usePeriodManager's performTransition calls this before resuming
  // EFFECTIVE (second half, either extra-time half) when a VAR/timeout
  // scoped stoppage is still open at the moment of a referee-driven period
  // transition — auto-close, not reject, since the operator cannot un-blow
  // the whistle. Mirrors the VAR-close half of useCockpitClockHandlers.ts's
  // handleGlobalClockStopGuarded (its own Detener-while-VAR sequence: log
  // VARStop, bank the VAR duration via handleVarToggleBase) — established
  // precedent in this codebase for "auto-close an open scoped stoppage as
  // part of a different operator action, then proceed." Timeout has no
  // separate local-state to bank (isTimeoutActive derives purely from
  // liveEvents/queuedEvents via useCockpitIneffectiveBreakdown), so closing
  // it is just logging TimeoutStop.
  const closeScopedStoppages = useCallback(() => {
    const kinds = computeScopedStoppageCloseKinds(
      breakdownVarActive || isVarActiveLocal,
      isTimeoutActive,
    );
    if (kinds.includes("VAR")) {
      logNeutralTimerEvent("VARStop");
      handleVarToggleBase({
        currentGlobalSeconds: parseClockToSeconds(globalClock),
        currentVarSeconds: varTimeSeconds,
        isGlobalClockRunning,
      });
    }
    if (kinds.includes("TIMEOUT")) {
      logNeutralTimerEvent("TimeoutStop");
    }
  }, [
    breakdownVarActive,
    globalClock,
    handleVarToggleBase,
    isGlobalClockRunning,
    isTimeoutActive,
    isVarActiveLocal,
    logNeutralTimerEvent,
    varTimeSeconds,
  ]);

  // ── Period manager ─────────────────────────────────────────────────────
  const globalTimeSeconds = parseClockToSeconds(globalClock);
  const {
    currentPhase,
    periodInfo,
    showExtraTimeAlert,
    isTransitionPending,
    transitionToHalftime,
    transitionToSecondHalf,
    transitionToFulltime,
    transitionToExtraFirst,
    transitionToExtraHalftime,
    transitionToExtraSecond,
    transitionToPenalties,
    finishMatch,
    dismissExtraTimeAlert,
  } = usePeriodManager(
    matchForPhase,
    effectiveTime,
    globalTimeSeconds,
    clockMode,
    isGlobalClockRunning,
    handleModeSwitch,
    fetchMatch,
    ({ target, error: transErr }) => {
      const message = `Failed to update status to ${target}. ${
        transErr instanceof Error ? transErr.message : ""
      }`;
      setToast({
        message,
        actionLabel: "Retry",
        action: () => {
          if (target === "Halftime")
            guardTransition("Halftime", transitionToHalftime);
          if (target === "Live_Second_Half")
            guardTransition("Live_Second_Half", transitionToSecondHalf);
          if (target === "Fulltime")
            guardTransition("Fulltime", transitionToFulltime);
        },
      });
    },
    isVarActive,
    isTimeoutActive,
    closeScopedStoppages,
  );

  // ── Broadcast-cap clock (presentation-only; PMA-COCK-003 broadcast model) ─
  // Main clock caps at each period's regulation end (45:00/90:00/105:00/
  // 120:00); stoppage time is shown separately as +MM:SS. Stopped/between
  // phases (Halftime, Fulltime, ...) freeze the main clock at the cap and
  // carry the just-ended period's added time via `lastLiveAddedSecondsRef`,
  // since `periodInfo.periodElapsedSeconds` resets to 0 once the phase is
  // no longer one of the four live periods.
  const BROADCAST_LIVE_PHASES: PeriodPhase[] = [
    "FIRST_HALF",
    "SECOND_HALF",
    "FIRST_HALF_EXTRA_TIME",
    "SECOND_HALF_EXTRA_TIME",
  ];
  const isBroadcastLivePhase = BROADCAST_LIVE_PHASES.includes(currentPhase);
  const liveBroadcastClock = isBroadcastLivePhase
    ? computeBroadcastClock(currentPhase, periodInfo.periodElapsedSeconds)
    : null;
  const lastLiveAddedSecondsRef = useRef(0);
  useEffect(() => {
    if (liveBroadcastClock) {
      lastLiveAddedSecondsRef.current = liveBroadcastClock.addedSeconds;
    }
  }, [liveBroadcastClock?.addedSeconds]);
  // COMPLETED and PENALTIES both show the match's real uncapped total, not
  // the just-ended live period's added time — mirrors the public page's
  // identical fix (`PublicMatchAnalytics.tsx`). See `computeBroadcastClock`'s
  // doc comment in clockHelpers.ts: COMPLETED is reachable from three
  // different prior periods, so a flat cap is never correct there; PENALTIES
  // is reachable from two (Fulltime directly, or via extra time —
  // PMA-COCK-199/EPIC-33), the same reason, corrected v1.184.0
  // (PMA-COCK-197).
  //
  // PMA-COCK-202/EPIC-37 correction: PENALTIES now passes `effectiveTime`
  // (the pure effective accumulator from `useMatchTimer`), NOT
  // `globalTimeSeconds` (`parseClockToSeconds(globalClock)`, itself
  // `effectiveTime + ineffectiveSeconds + varSeconds`). v1.184.0 assumed
  // `globalTimeSeconds` "is already the frozen banked total" once PENALTIES
  // is current — true only when banked ineffective/VAR time is exactly
  // zero, which every PMA-COCK-197/199 test happened to use.
  // `effectiveTime` is the value PMA-MATCH-030 actually freezes into
  // `clock_seconds_at_period_start`/`match_time_seconds` on entering
  // PENALTIES (confirmed via live instrumentation, EPIC-37: a match with
  // real banked ineffective time before the shootout showed e.g. "120:28"
  // instead of the persisted "120:00" — not live drift, the value was
  // static tick-by-tick throughout the shootout, but this fixed
  // mis-composition). `current_period_start_timestamp` still gates the
  // freeze exactly as before (PMA-MATCH-030 unaffected) — only which number
  // is shown as frozen changes.
  //
  // COMPLETED (EPIC-39/PMA-COCK-206): now ALSO passes `effectiveTime`, via
  // `resolveBroadcastArgument`'s shared `PENALTIES`/`COMPLETED` branch.
  // PMA-COCK-202 had confirmed (but deliberately left unfixed, out of
  // EPIC-37's PENALTIES-only scope) that COMPLETED shared this exact same
  // latent defect: a genuinely finished match's `effectiveTime` is what a
  // completion freeze actually banks, so feeding `globalTimeSeconds` here
  // double-counted banked ineffective time on top of an already-real total
  // (proven directly against this file's own `useMatchTimer`/
  // `parseClockToSeconds` composition — not inferred from the public page —
  // in `CockpitContext.completedClock.test.ts`; e.g. 5934s effective + 200s
  // banked ineffective showed "102:14" instead of the real "98:54"). Every
  // other stopped phase (Halftime, Fulltime, ...) keeps carrying the
  // just-ended period's added time via `lastLiveAddedSecondsRef`, unchanged.
  const broadcastClock =
    liveBroadcastClock ??
    computeBroadcastClock(
      currentPhase,
      resolveBroadcastArgument(
        currentPhase,
        effectiveTime,
        globalTimeSeconds,
        lastLiveAddedSecondsRef.current,
      ),
    );
  // Events stamp `match_clock` with the broadcast MAIN clock (so a goal at
  // TV-56' reads 56'), formatted like every other match_clock value.
  // Stamped match_clock preserves stoppage in the stored value (Bug 3, "stop
  // the data loss going forward") — deliberately NOT the same as the
  // on-screen `broadcastClock.mainClock`/`.addedClock` split, which is
  // unaffected. See `formatStampedMatchClock`'s doc comment for why.
  const broadcastMatchClock = formatStampedMatchClock(broadcastClock);
  broadcastMatchClockRef.current = broadcastMatchClock;

  // ── Action flow ────────────────────────────────────────────────────────
  const {
    currentStep,
    currentTeam,
    selectedPlayer,
    selectedAction,
    attackedGoalMouthEdge,
    fieldAnchor,
    availableActions,
    availableOutcomes,
    positionMode,
    setPositionMode,
    handlePlayerClick,
    handleZoneSelect,
    handleQuickActionSelect,
    handleOpenMoreActions,
    handleDestinationClick,
    handleActionClick,
    handleOutcomeClick,
    handleRecipientClick,
    // Client wave 2026-09-19 ("Tiro al palo"), out-of-band prop-threading
    // addition — same precedent as `handleUpdateGoalPlacement`/
    // `handleStaffSelection` above: this wave's ownership table didn't list
    // this file, but the new handler is produced by `useActionFlow` (fe-shot
    // owned) and consumed by `ActionStage.tsx` (fe-shot owned); threading it
    // through this shared context is the only path between them. No other
    // logic in this file changed. Recorded in team_findings.md.
    handleWoodworkPlace,
    // EPIC-98 ("Tiro Penal" goal-frame screen), same out-of-band
    // prop-threading precedent as `handleWoodworkPlace` immediately above —
    // this wave's ownership table (`team_plan.md`) didn't list this file
    // either. Recorded in team_findings.md.
    handlePenaltyKickDispatch,
    resetFlow,
    currentStepRef,
    chipState,
    handleChipTap,
    dismissChipPanel,
    handlePlacementTap,
    placedPlacement,
  } = useActionFlow({
    match,
    matchClock: broadcastMatchClock,
    operatorPeriod,
    selectedTeam,
    isSubmitting,
    cardYellowCounts,
    expelledPlayerIds,
    recentEvents: [...liveEvents, ...queuedEvents],
    onIneffectiveTrigger: (payload) => {
      const note = payload.note
        ? payload.note
        : payload.actionType === "FreeKick"
          ? t("ineffectiveReasonFreeKick", "Tiros libres")
          : payload.actionType === "ThrowIn"
            ? t("ineffectiveReasonThrowIn", "Laterales")
            : payload.actionType === "Corner"
              ? t("ineffectiveReasonCorner", "Córner")
              : payload.actionType === "GoalKick"
                ? t("ineffectiveReasonGoalKick", "Saque de puerta")
                : payload.actionType === "Goal"
                  ? t("ineffectiveReasonGoal", "Gol")
                  : t("ineffectiveReasonOther", "Otros");
      beginIneffective(note, {
        teamId: payload.teamId,
        playerId: payload.playerId,
        actionType: payload.actionType,
      });
    },
    sendEvent,
    // EPIC-98: `handlePenaltyKickDispatch`'s Atajado GK-credit lookup needs
    // to know who is actually on the pitch, not just on the roster — same
    // `onFieldIds` this file already computes above (`useOnFieldRoster`).
    onFieldIds,
  });

  // ── Expelled player effect ─────────────────────────────────────────────
  useCockpitExpelledPlayerEffect({
    selectedPlayer,
    expelledPlayerIds,
    resetFlow,
    setToast,
    t,
  });

  // ── Transition state ──────────────────────────────────────────────────
  const {
    currentStatusNormalized,
    transitionError,
    guardTransition,
    cockpitLocked,
    lockReason,
    transitionDisabled,
    penaltiesTransitionDisabled,
    transitionReason,
  } = useCockpitTransitionState({
    statusOverride,
    currentPhase,
    globalTimeSeconds,
    match,
    isAdmin,
    t,
    isTransitionPending,
  });

  // Kickoff lineup pre-check (PMA-COCK-089, EPIC-08 Phase 1). Routes the
  // genuine kickoff (Pending/Scheduled -> Live_First_Half) through the same
  // guardTransition used by every other period transition, so it gets the
  // friendly "11 starters required" message via the existing
  // transitionError UI slot instead of a silent no-op / raw backend
  // rejection. This is a client-side UX pre-check only — both backend
  // status routes remain authoritative and independently reject the
  // transition with the same "11 starters" condition.
  //
  // A same-status clock resume (match already live) must NOT be routed
  // through guardTransition — see isKickoffAttemptStatus's doc comment for
  // why (PMA-COCK-089 regression). Verified the Scheduled leg doesn't newly
  // regress: Scheduled and Pending both map to PeriodPhase "NOT_STARTED"
  // (usePeriodManager.ts:52-54), so guardTransition's phaseDerivedStatus
  // resolves to "Pending" for a Scheduled match too, and the FSM's explicit
  // `Scheduled: ["Live_First_Half"]` entry (useTransitionGuards.ts:98)
  // covers it either way — a Scheduled kickoff passes guardTransition
  // exactly like a Pending one.
  const handleGlobalClockStartChecked = useCallback(() => {
    if (isKickoffAttemptStatus(match?.status)) {
      guardTransition("Live_First_Half", handleGlobalClockStart);
    } else {
      handleGlobalClockStart();
    }
  }, [match?.status, guardTransition, handleGlobalClockStart]);

  // ── Interaction handlers ──────────────────────────────────────────────
  const {
    handleCardSelection,
    cancelCardSelection,
    handlePlayerSelection,
    logCardForPlayer,
    logCardForStaff,
    handleFieldPlayerSelection,
    handleFieldDestination,
    handleQuickSubstitution,
    handleActionClickOverride,
    handleOutcomeSelect,
    handleRecipientSelect,
    eligibleRecipients,
  } = useCockpitInteractionHandlers({
    t,
    match,
    cockpitLocked,
    selectedTeam,
    setSelectedTeam,
    pendingCardType,
    pendingCardTypeRef,
    setPendingCardType,
    resetFlow,
    operatorPeriod,
    broadcastMatchClock,
    sendEvent,
    expelledPlayerIds,
    isVarActive,
    currentStep,
    selectedPlayer,
    selectedAction,
    currentTeam,
    onFieldIds,
    setSubstitutionTeam,
    setShowSubstitutionFlow,
    setPriorityPlayerId,
    handlePlayerClick,
    handleDestinationClick,
    handleActionClick,
    handleOutcomeClick,
    handleRecipientClick,
    beginIneffective,
    showToast: (message) => showTimedToast(message),
    setSubstitutionInitialPlayerOn,
    setSubstitutionInitialPlayerOff,
  });

  // ── Substitution flow ─────────────────────────────────────────────────
  const {
    modalTeam: substitutionModalTeam,
    availablePlayers: substitutionAvailablePlayers,
    squadPlayers: substitutionSquadPlayers,
    onField: substitutionOnField,
    onSubmit: handleSubstitutionSubmitBase,
    onCancel: handleSubstitutionCancel,
    isSubmittingSquadPlayer: substitutionIsSubmittingSquadPlayer,
    squadSubmitError: substitutionSquadSubmitError,
  } = useCockpitSubstitutionFlow({
    match,
    substitutionTeam,
    onFieldIds,
    showSubstitutionFlow,
    cockpitLocked,
    expelledPlayerIds,
    broadcastMatchClock,
    operatorPeriod,
    applyOnFieldChange,
    sendEvent,
    setShowSubstitutionFlow,
    showTimedToast,
    t,
    fetchMatch,
  });

  // ── Penalty shootout ──────────────────────────────────────────────────
  const { shootout, logShootoutKick } = useCockpitShootout({
    match,
    liveEvents,
    queuedEvents,
    matchClock: broadcastMatchClock,
    operatorPeriod,
    cockpitLocked,
    sendEvent,
  });

  const handleSubstitutionSubmit = useCallback(
    (playerOffId: string, playerOnId: string, isConcussion: boolean) => {
      resetSubstitutionPrefill();
      handleSubstitutionSubmitBase(playerOffId, playerOnId, isConcussion);
      applyTacticalSubstitution(playerOffId, playerOnId);
    },
    [
      resetSubstitutionPrefill,
      handleSubstitutionSubmitBase,
      applyTacticalSubstitution,
    ],
  );

  const handleSubstitutionCancelWithClear = useCallback(() => {
    resetSubstitutionPrefill();
    handleSubstitutionCancel();
  }, [resetSubstitutionPrefill, handleSubstitutionCancel]);

  useEffect(() => {
    if (viewMode === "logger") return;
    resetSubstitutionPrefill();
  }, [viewMode, resetSubstitutionPrefill]);

  useEffect(() => {
    resetSubstitutionPrefill();
  }, [matchId, resetSubstitutionPrefill]);

  // ── Keyboard handlers ─────────────────────────────────────────────────
  const { buffer } = useCockpitKeyboardHandlers({
    isSubmitting,
    cockpitLocked,
    currentStep,
    match,
    currentTeam,
    selectedTeam,
    eligibleRecipients,
    availableOutcomes,
    isBallInPlay,
    setIsBallInPlay,
    setSelectedTeam,
    setPriorityPlayerId,
    handlePlayerClick,
    handleRecipientClick,
    handleOutcomeClick,
    handleQuickActionSelect,
    handleActionClick,
    resetFlow,
    handleGlobalClockStart: handleGlobalClockStartChecked,
    handleGlobalClockStop,
  });

  // ── Lifecycle effects ──────────────────────────────────────────────────
  useCockpitLifecycleEffects({
    matchId,
    lastTimelineRefreshRequest,
    setCurrentMatch,
    hydrateEvents,
    resetDuplicateStats,
    setManualFieldFlip,
    match,
    statusOverride,
    resetOperatorControls,
    cockpitLocked,
    resetFlow,
  });

  // Re-fetch match document when another tab changes match state
  useEffect(() => {
    if (!lastMatchRefreshRequest) return;
    fetchMatch();
  }, [lastMatchRefreshRequest, fetchMatch]);

  // ── Clock handlers (guarded) ──────────────────────────────────────────
  const {
    handleGlobalClockStartGuarded,
    handleGlobalClockStopGuarded,
    handleModeSwitchGuarded,
    handleVarToggle,
    handleTimeoutToggle,
    showFieldResume,
  } = useCockpitClockHandlers({
    cockpitLocked,
    clockMode,
    currentPhase,
    isVarActiveLocal,
    isVarActive,
    isTimeoutActive,
    varTimeSeconds,
    globalClock,
    isGlobalClockRunning,
    beginIneffective,
    endIneffectiveIfNeeded,
    setIsBallInPlay,
    handleGlobalClockStart: handleGlobalClockStartChecked,
    handleGlobalClockStop,
    handleGlobalClockStartVarSync,
    handleGlobalClockStopVarSync,
    handleVarToggleBase,
    logNeutralTimerEvent,
    handleModeSwitch,
    showTimedToast,
    t,
  });

  // ── Referee actions ───────────────────────────────────────────────────
  const handleRefereeAction = useCallback(
    (actionId: string) => {
      const homeTeamId = match?.home_team.team_id ?? match?.home_team.id;
      const awayTeamId = match?.away_team.team_id ?? match?.away_team.id;

      if (actionId === "injury-home" && homeTeamId) {
        beginIneffective(t("ineffectiveReasonInjury", "Lesión"), {
          teamId: homeTeamId,
          playerId: null,
          actionType: "Injury",
        });
        return;
      }

      if (actionId === "injury-away" && awayTeamId) {
        beginIneffective(t("ineffectiveReasonInjury", "Lesión"), {
          teamId: awayTeamId,
          playerId: null,
          actionType: "Injury",
        });
        return;
      }

      /**
       * @deprecated PMA-COCK-118 — these ids ("interference", "discussion",
       * "injury", "equipment") were sent by RefereeActionBar's now-removed
       * neutral-reason buttons, collapsed into the existing
       * `btn-timeout-toggle` control. No live caller sends them anymore;
       * kept as a harmless fallback (same shape as `REFEREE_ACTIONS` in
       * `RefereeActionBar.tsx`) rather than deleted.
       */
      const actionLabels: Record<string, string> = {
        interference: t(
          "refereeActionInterference",
          "Ball hits referee / interference",
        ),
        discussion: t(
          "refereeActionDiscussion",
          "Referee discussion / explanation",
        ),
        injury: t("refereeActionInjury", "Referee injury"),
        equipment: t(
          "refereeActionEquipment",
          "Equipment / communication issue",
        ),
      };
      const note =
        actionLabels[actionId] || t("ineffectiveReasonOther", "Other");
      beginIneffective(note, {
        teamId: "NEUTRAL",
        playerId: null,
        actionType: "Other",
        isNeutral: true,
      });
    },
    [beginIneffective, match, t],
  );

  // ── E2E seed ──────────────────────────────────────────────────────────
  useCockpitE2EPlayersSeed({ enabled: IS_E2E_TEST_MODE, match });

  // ── Live score ─────────────────────────────────────────────────────────
  // PMA-COCK-205 (EPIC-43, judge round 1, P7): pass queuedEvents too, or a
  // goal disappears from the scoreboard for its entire pending-resend
  // window (see useLiveScore.ts's own comment and
  // useMatchSocket.liveScoreQueuedRegression.test.ts).
  const { goalEvents, liveScore, formatGoalLabel } = useLiveScore(
    match,
    liveEvents,
    queuedEvents,
  );

  // ── Auto effects ──────────────────────────────────────────────────────
  useCockpitAutoEffects({
    t,
    liveEvents,
    duplicateHighlight,
    clearDuplicateHighlight,
    beginIneffective,
  });

  // ── Harness events ────────────────────────────────────────────────────
  const { sendHarnessPassEvent, sendHarnessRawEvent } = useCockpitHarnessEvents(
    {
      match,
      operatorClock,
      operatorPeriod,
      sendEvent,
    },
  );

  // ── Event handlers ────────────────────────────────────────────────────
  const {
    undoError,
    undoDisabled,
    handleUndoLastEvent,
    handleDeletePendingEvent,
    handleDeleteLoggedEvent,
    handleUpdateEventNotes,
    handleUpdateEventData,
    handleVarCornerToGoalKick,
    handleUpdateGoalPlacement,
  } = useCockpitEventHandlers({
    t,
    isAdmin,
    isConnected,
    cockpitLocked,
    lockReason,
    pendingAcks,
    undoStack,
    liveEvents,
    queuedEvents,
    removeLiveEventByClientId,
    removeQueuedEvent,
    removeUndoCandidate,
    undoEvent,
    removeQueuedEventByClientId,
    rejectPendingAck,
    removeLiveEventById,
    hydrateEvents,
    updateEventNotes,
    upsertLiveEvent,
    showToast: (message) => showTimedToast(message),
  });

  // ── E2E harness ───────────────────────────────────────────────────────
  useCockpitHarness({
    enabled: IS_E2E_TEST_MODE,
    match,
    resetFlow,
    setSelectedTeam,
    currentStepRef,
    sendPassEvent: sendHarnessPassEvent,
    sendRawEvent: sendHarnessRawEvent,
    undoLastEvent: handleUndoLastEvent,
    computedDriftSeconds,
    forcedDriftSeconds,
    driftSeconds,
    showDriftNudge,
    isBallInPlay,
    effectiveClock,
    ineffectiveClock,
  });

  // ── Duplicate telemetry ───────────────────────────────────────────────
  const {
    lastDuplicateTeamName,
    lastDuplicateSeenAt,
    lastDuplicateSummaryDetails,
    lastDuplicateSummaryDefault,
    duplicateSessionDetailsSuffix,
    duplicateSessionSummaryDefault,
    duplicateDetailsDefault,
    duplicateExistingEventDefault,
  } = useDuplicateTelemetry(match, duplicateStats, duplicateHighlight);

  // ── Reset match ───────────────────────────────────────────────────────
  const {
    showResetModal,
    setShowResetModal,
    resetConfirmText,
    setResetConfirmText,
    isCompletedOrFulltime,
    resetBlocked,
    resetDisabledReason,
    resetTooltip,
    openResetModal,
    confirmGlobalReset,
  } = useResetMatch({
    matchId,
    isAdmin,
    match,
    queuedCount,
    pendingAckCount,
    t,
    setMatch,
    hydrateEvents,
    resetVarState,
    resetOperatorControls,
    setLiveEvents,
    clearQueuedEvents,
    clearPendingAcks,
    clearUndoStack,
    resetDuplicateStats,
    clearDuplicateHighlight,
  });

  // ── Manual labels ─────────────────────────────────────────────────────
  const manualHomeTeamLabel = match?.home_team.name || t("homeTeam", "Home");
  const manualAwayTeamLabel = match?.away_team.name || t("awayTeam", "Away");

  // ── Context value ─────────────────────────────────────────────────────
  const value = useMemo(
    () => ({
      t,
      isLoggerReady,
      matchId,
      isAdmin,
      match,
      setMatch,
      loading,
      error,
      fetchMatch,
      hydrateEvents,
      isConnected,
      liveEvents,
      queuedEvents,
      failedEvents,
      pendingAcks,
      undoStack,
      duplicateHighlight,
      duplicateStats,
      operatorPeriod,
      sendEvent,
      isSubmitting,
      queuedCount,
      failedCount,
      pendingAckCount,
      selectedTeam,
      setSelectedTeam,
      onFieldIds,
      shootout,
      logShootoutKick,
      broadcastMatchClock,
      globalClock,
      effectiveClock,
      effectiveTime,
      ineffectiveClock,
      clockMode,
      isGlobalClockRunning,
      currentPhase,
      periodInfo,
      broadcastClock,
      showExtraTimeAlert,
      transitionToHalftime,
      transitionToSecondHalf,
      transitionToFulltime,
      transitionToExtraFirst,
      transitionToExtraHalftime,
      transitionToExtraSecond,
      transitionToPenalties,
      finishMatch,
      dismissExtraTimeAlert,
      currentStatusNormalized,
      transitionError,
      guardTransition,
      cockpitLocked,
      lockReason,
      transitionDisabled,
      penaltiesTransitionDisabled,
      transitionReason,
      statusOverride,
      matchForPhase,
      ineffectiveNoteOpen,
      ineffectiveNoteText,
      setIneffectiveNoteText,
      ineffectiveActionType,
      setIneffectiveActionType,
      ineffectiveTeamSelection,
      setIneffectiveTeamSelection,
      ineffectiveActionDropdownOpen,
      setIneffectiveActionDropdownOpen,
      ineffectiveTeamDropdownOpen,
      setIneffectiveTeamDropdownOpen,
      hasActiveIneffective,
      cancelIneffectiveNote,
      confirmIneffectiveNote,
      beginIneffective,
      isVarActive,
      varTimeClock,
      isTimeoutActive,
      timeoutTimeClock,
      varTimeSeconds,
      timeoutTimeSeconds,
      handleGlobalClockStartGuarded,
      handleGlobalClockStopGuarded,
      handleModeSwitchGuarded,
      handleVarToggle,
      handleTimeoutToggle,
      showFieldResume,
      handleRefereeAction,
      ineffectiveBreakdown,
      isBallInPlay,
      showDriftNudge,
      driftSeconds,
      cardDisciplinaryStatus,
      expelledPlayerIds,
      goalCountByPlayerId,
      currentStep,
      currentTeam,
      selectedPlayer,
      selectedAction,
      attackedGoalMouthEdge,
      fieldAnchor,
      availableActions,
      availableOutcomes,
      positionMode,
      setPositionMode,
      handleZoneSelect,
      handleQuickActionSelect,
      handleOpenMoreActions,
      // Client wave 2026-09-19 ("Tiro al palo") — see this hook's own
      // destructure comment above.
      handleWoodworkPlace,
      // EPIC-98 ("Tiro Penal" goal-frame screen) — see this hook's own
      // destructure comment above.
      handlePenaltyKickDispatch,
      resetFlow,
      handleCardSelection,
      cancelCardSelection,
      handlePlayerSelection,
      logCardForPlayer,
      logCardForStaff,
      handleFieldPlayerSelection,
      handleFieldDestination,
      handleQuickSubstitution,
      handleActionClickOverride,
      handleOutcomeSelect,
      handleRecipientSelect,
      eligibleRecipients,
      undoError,
      undoDisabled,
      handleUndoLastEvent,
      handleDeletePendingEvent,
      handleDeleteLoggedEvent,
      handleUpdateEventNotes,
      handleUpdateEventData,
      handleVarCornerToGoalKick,
      handleUpdateGoalPlacement,
      buffer,
      getDisplayPosition,
      handleTacticalPlayerDragEnd,
      draggingPlayerId,
      setDraggingPlayerId,
      allTacticalPositions: tacticalPositions,
      homeFormation,
      awayFormation,
      applyFormation,
      viewMode,
      setViewMode,
      showSubstitutionFlow,
      substitutionModalTeam,
      substitutionInitialPlayerOn,
      substitutionInitialPlayerOff,
      substitutionAvailablePlayers,
      substitutionSquadPlayers,
      substitutionOnField,
      substitutionIsSubmittingSquadPlayer,
      substitutionSquadSubmitError,
      handleSubstitutionSubmit,
      handleSubstitutionCancel: handleSubstitutionCancelWithClear,
      manualFieldFlip,
      setManualFieldFlip,
      manualFlipOverride,
      homeAttackingLeft,
      setHomeAttacksRightFirstHalf,
      pendingCardType,
      priorityPlayerId,
      goalEvents,
      liveScore,
      formatGoalLabel,
      toast,
      dismissToast,
      lastDuplicateTeamName,
      lastDuplicateSeenAt,
      lastDuplicateSummaryDetails,
      lastDuplicateSummaryDefault,
      duplicateSessionDetailsSuffix,
      duplicateSessionSummaryDefault,
      duplicateDetailsDefault,
      duplicateExistingEventDefault,
      resetDuplicateStats,
      clearDuplicateHighlight,
      showResetModal,
      setShowResetModal,
      resetConfirmText,
      setResetConfirmText,
      isCompletedOrFulltime,
      resetBlocked,
      resetDisabledReason,
      resetTooltip,
      openResetModal,
      confirmGlobalReset,
      undoCount: undoStack.length,
      manualHomeTeamLabel,
      manualAwayTeamLabel,
      isMatchLive,
      chipState,
      handleChipTap,
      dismissChipPanel,
      handlePlacementTap,
      placedPlacement,
    }),
    [
      match,
      loading,
      error,
      isConnected,
      liveEvents,
      queuedEvents,
      failedEvents,
      pendingAcks,
      undoStack,
      duplicateHighlight,
      duplicateStats,
      operatorPeriod,
      isSubmitting,
      queuedCount,
      failedCount,
      pendingAckCount,
      selectedTeam,
      onFieldIds,
      shootout,
      logShootoutKick,
      broadcastMatchClock,
      globalClock,
      effectiveClock,
      effectiveTime,
      ineffectiveClock,
      clockMode,
      isGlobalClockRunning,
      currentPhase,
      periodInfo,
      broadcastClock,
      showExtraTimeAlert,
      currentStatusNormalized,
      transitionError,
      cockpitLocked,
      lockReason,
      transitionDisabled,
      penaltiesTransitionDisabled,
      transitionReason,
      statusOverride,
      matchForPhase,
      ineffectiveNoteOpen,
      ineffectiveNoteText,
      ineffectiveActionType,
      ineffectiveTeamSelection,
      ineffectiveActionDropdownOpen,
      ineffectiveTeamDropdownOpen,
      hasActiveIneffective,
      isVarActive,
      varTimeClock,
      isTimeoutActive,
      timeoutTimeClock,
      varTimeSeconds,
      timeoutTimeSeconds,
      showFieldResume,
      ineffectiveBreakdown,
      isBallInPlay,
      showDriftNudge,
      driftSeconds,
      cardDisciplinaryStatus,
      expelledPlayerIds,
      goalCountByPlayerId,
      currentStep,
      currentTeam,
      selectedPlayer,
      selectedAction,
      attackedGoalMouthEdge,
      fieldAnchor,
      availableActions,
      availableOutcomes,
      positionMode,
      eligibleRecipients,
      undoError,
      undoDisabled,
      buffer,
      draggingPlayerId,
      tacticalPositions,
      homeFormation,
      awayFormation,
      viewMode,
      showSubstitutionFlow,
      substitutionModalTeam,
      substitutionAvailablePlayers,
      substitutionSquadPlayers,
      substitutionOnField,
      substitutionIsSubmittingSquadPlayer,
      substitutionSquadSubmitError,
      substitutionInitialPlayerOff,
      manualFieldFlip,
      manualFlipOverride,
      homeAttackingLeft,
      setHomeAttacksRightFirstHalf,
      pendingCardType,
      priorityPlayerId,
      substitutionInitialPlayerOn,
      goalEvents,
      liveScore,
      toast,
      showResetModal,
      resetConfirmText,
      isCompletedOrFulltime,
      resetBlocked,
      resetDisabledReason,
      resetTooltip,
      manualHomeTeamLabel,
      manualAwayTeamLabel,
      lastDuplicateTeamName,
      lastDuplicateSeenAt,
      lastDuplicateSummaryDetails,
      lastDuplicateSummaryDefault,
      duplicateSessionDetailsSuffix,
      duplicateSessionSummaryDefault,
      duplicateDetailsDefault,
      duplicateExistingEventDefault,
      chipState,
      handleChipTap,
      dismissChipPanel,
      handlePlacementTap,
      placedPlacement,
    ],
  );

  return (
    <CockpitContext.Provider value={value}>{children}</CockpitContext.Provider>
  );
}
